/**
 * Data-visualisation scales.
 *
 * ⚠️ PROVISIONAL — this is the ONE token file not mirrored from Figma. Three of the five values
 * below were derived here, not exported from the design file, because the design system has no
 * sequential data-viz scale yet (open since 2026-07-13; see docs/OPEN_ITEMS.md). They are here so
 * components stay tokens-only (HARD RULE 1) instead of carrying raw hex, and so the swap is a
 * one-file change once the real scale is defined in Figma.
 *
 * Do NOT copy these into `primitives.ts` — that file is a 1:1 mirror of Figma Variables and adding
 * invented values would corrupt the mirror.
 */
import { base } from './primitives';

/**
 * Sequential scale — low to high, for encoding "how much" as colour intensity (choropleth fills,
 * heatmaps, density). NOT for telling categories apart; that is `chartTheme.seriesPalette`.
 *
 * Built to two rules, both of which the previous ad-hoc ramp broke:
 *
 * 1. **Perceptually even.** Steps are 18.5 / 18.7 / 19.0 / 18.3 in L* (perceptual lightness,
 *    0 = black, 100 = white), i.e. even to within 4%. The old ramp — the purple *surface tints*
 *    borrowed from UI states — stepped 5.7 / 16.4 / 34.2, so its biggest step was 6× its smallest:
 *    the two palest buckets merged into one wash and the map exaggerated differences at the top
 *    while hiding them at the bottom.
 * 2. **Chroma holds through the middle.** The scale is routed THROUGH the brand purple rather than
 *    interpolated straight from near-white to a dark end, which would collapse to grey mush around
 *    the midpoint (#bdb7d8). Steps 1 and 4 are the real brand tokens; only 2, 3 and 5 are derived.
 *
 * Five steps because that is the low end of the 5–7 a choropleth normally wants, and because it
 * lands the brand purple exactly on step 4.
 */
export const sequential = [
  base.purple008, //           L* 94.6  — existing token
  '#beb2ff', //                L* 76.1  — PROVISIONAL
  '#8c74ff', //                L* 57.4  — PROVISIONAL
  base.purple, //              L* 38.4  — existing token
  '#330993', //                L* 20.1  — PROVISIONAL, the dark end the tint ramp never had
] as const;

/*
 * Two ink splits, not one — because the WCAG floor depends on WHAT is drawn on the fill.
 *
 * The scale spans near-white to near-black, so no single ink works across it. Measured, dark ink
 * (`grey080`) scores 15.1 / 9.0 / 5.0 / 2.5 / 1.3 down the ramp and white is the mirror image,
 * 1.2 / 1.9 / 3.5 / 6.9 / 13.1. The two sets below flip at DIFFERENT steps, and that is not an
 * inconsistency: a graphical object needs 3:1 (WCAG 1.4.11) and body text needs 4.5:1 (1.4.3), so
 * step 3 — where dark scores 4.98 and white 3.47 — is a free choice for a stroke and a forced one
 * for text. One set served both until 2026-09-28 only because the split points happened to agree.
 */

/**
 * Fills that need a LIGHT overlay/outline — GRAPHICAL objects, 3:1 floor.
 *
 * Step 3 is in this set by measurement rather than by the WCAG ratio (designer, 2026-09-28). Both
 * inks clear 3:1 on it, so the tie is broken by APCA — the WCAG 3 candidate, which models the
 * perceptual failure of dark ink on saturated mid-tones that WCAG 2.x's pure luminance ratio
 * cannot see. APCA scores white Lc 67.4 against dark Lc 40.3 on this step: a 27-point margin, and
 * dark falls below APCA's own Lc 45 floor for large text. Worst case in this set is now 3.47:1,
 * still clear of 3:1.
 */
export const sequentialDarkSteps: ReadonlySet<string> = new Set([sequential[2], sequential[3], sequential[4]]);

/**
 * Categorical palette — one colour per SERIES, for telling things apart. The opposite job to
 * `sequential` above, which says "how much".
 *
 * **Where these come from.** They are the hues the production application already draws its
 * charts with (designer, 2026-09-21). The design system never owned a categorical palette, so
 * `seriesPalette` borrowed six `intent.*.solid` hues as a placeholder — which is why every chart
 * in Storybook has looked different from the same chart in the product since 2026-07-13. These
 * are reproduced here exactly as production has them, so developers can switch onto the design
 * system without a visual change; refining them afterwards is then a one-file edit.
 *
 * They live in this file rather than `primitives.ts` for the same reason `sequential` does:
 * `primitives.ts` is a 1:1 mirror of Figma Variables, and these were never in Figma.
 *
 * **Two things to revisit when the palette is refined** (both are properties of the colours, not
 * of the order, so no re-ordering can fix them):
 *
 * - `corn`, `lavender`, `canary`, `lightBlue` and `tomato` sit below the 3:1 contrast floor
 *   against a white chart surface — `corn` and `lavender` at ~1.3:1 are close to invisible as a
 *   thin line. They are ordered LAST for exactly this reason (see below), so a chart only
 *   reaches them past eight series.
 * - `creme` and `lavender` are so low in chroma that they read as grey, which on a chart is the
 *   colour of "gridline" or "disabled series" rather than of a series.
 */
const categorical = {
  magenta: '#A83DBA',
  peach: '#EF5350',
  blue: '#368BC9',
  plum: '#570087',
  creme: '#B38773',
  indigo: '#3B52D9',
  pink: '#EC407A',
  purple: '#7E57C2',
  canary: '#FFA600',
  lightBlue: '#25BCCF',
  corn: '#FEDF71',
  tomato: '#FF7043',
  lavender: '#F0D5FF',
  /**
   * Production carries this name too (spelled `ordchid` until UIG-1220), holding the SAME value as `purple`
   * (#7E57C2). Kept for parity and spelled correctly here, but deliberately absent from
   * `categoricalOrder` — two slots painting one colour is not a categorical palette. Worth
   * checking whether one of the two was meant to be a different hue.
   */
  orchid: '#7E57C2',
} as const;

/**
 * The ORDER series are assigned in. Not alphabetical and not the order they were written in —
 * computed, because with a categorical palette the order IS the accessibility.
 *
 * Two rules, in this priority:
 *
 * 1. **The eight that survive a white background come first.** Most charts have two to four
 *    series, so the first few slots carry almost all the traffic. The original order opened
 *    `canary` → `corn`, i.e. amber followed by pale yellow at 1.3:1 — two series that a
 *    sighted reader on a white card cannot separate, in the two slots that matter most.
 * 2. **Then maximise the separation of ADJACENT slots**, measured as OKLab ΔE under normal
 *    vision and under simulated deuteranopia, protanopia and tritanopia, taking the worst case.
 *    Neighbours are what a reader compares.
 *
 * Result: worst adjacent pair is ΔE 22.9 normal (floor 15) and 13.3 under protanopia (floor 8),
 * both comfortably clear. As authored the palette failed both — 13.3 normal between `corn` and
 * `canary`, and 4.9 under protanopia between `magenta` and `indigo`.
 *
 * **Changing this order is a real change, not a preference.** Re-run the check before shipping
 * one; the recipe is in docs/DECISIONS.md (2026-09-21).
 */
const categoricalOrder = [
  categorical.magenta,
  categorical.peach,
  categorical.blue,
  categorical.plum,
  categorical.creme,
  categorical.indigo,
  categorical.pink,
  categorical.purple,
  // From here the colours no longer clear 3:1 on white. A chart reaching this far needs its
  // series labelled rather than identified by colour alone — which is also the point at which
  // it has too many series to read, and should fold the tail into "Other".
  categorical.canary,
  categorical.lightBlue,
  categorical.corn,
  categorical.tomato,
  categorical.lavender,
] as const;

export { categorical, categoricalOrder };

export const dataViz = {
  sequential,
  sequentialDarkSteps,
  categorical,
  categoricalOrder,
} as const;

export type DataVizTokens = typeof dataViz;
